import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { CheckCircle, Clock, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { BookingPageLayout } from '@features/booking/layouts';
import { useBooking } from '@features/booking/state';
import { useRestaurant } from '@core/tenant/TenantProvider';
import { useAuth } from '@core/auth/AuthProvider';
import { useAnalytics, ANALYTICS_EVENTS } from '@core/analytics';
import { useLanguage } from '@core/i18n';
import { saveReservationNote, saveCompanions } from '@features/booking/api/reservations';
import { CompanionInputs } from '@features/booking/components/CompanionInputs';
import { GuestDetailsCard } from '@features/booking/components/GuestDetailsCard';
import { SummaryRow } from '@features/booking/components/SummaryRow';
import { useRegularCompanions } from '@features/booking/hooks/useRegularCompanions';
import { resolvePairingName } from '@features/booking/utils/pairings';
import type { CompanionData } from '@features/booking/api/reservations';
import type { RegularCompanion } from '@features/booking/api/companions';

const OCCASIONS = [
  { value: 'birthday', labelKey: 'BUY_A_GIFTCARD_OCCASION_STRING_1' },
  { value: 'anniversary', labelKey: 'BUY_A_GIFTCARD_OCCASION_STRING_2' },
  { value: 'business', labelKey: 'BUY_A_GIFTCARD_OCCASION_STRING_3' },
  { value: 'trip', labelKey: 'BUY_A_GIFTCARD_OCCASION_STRING_4' },
  { value: 'thankyou', labelKey: 'BUY_A_GIFTCARD_OCCASION_STRING_5' },
  { value: 'gift', labelKey: 'BUY_A_GIFTCARD_OCCASION_STRING_6' },
] as const;

const SERVICE_LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
  { value: 'pt', label: 'Português' },
] as const;

const BookingFinalPage = () => {
  const { t } = useTranslation('booking');
  const navigate = useNavigate();
  const booking = useBooking();
  const restaurant = useRestaurant();
  const { authState } = useAuth();
  const { currentLanguage } = useLanguage();
  const { trackEvent } = useAnalytics();

  const { data: regularCompanions } = useRegularCompanions();

  const [note, setNote] = useState('');
  const [occasion, setOccasion] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [languageService, setLanguageService] = useState('');
  const [companions, setCompanions] = useState<CompanionData[]>([]);
  const [availableRegulars, setAvailableRegulars] = useState<RegularCompanion[]>([]);
  const [regularsInitialized, setRegularsInitialized] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    trackEvent({ name: ANALYTICS_EVENTS.BOOKING_COMPLETE });
  }, [trackEvent]);

  // Initialize available regular companions once from query data
  useEffect(() => {
    if (regularCompanions.length > 0 && !regularsInitialized) {
      setAvailableRegulars(regularCompanions);
      setRegularsInitialized(true);
    }
  }, [regularCompanions, regularsInitialized]);

  const { dateLocale } = useLanguage();

  const isWaitingList = booking.flowType === 'waiting_list';
  const isEarlyAccess = booking.isEarlyAccessFlow;

  const displayCurrency = restaurant.reservationGuaranteeCurrency ?? restaurant.currency ?? 'USD';
  const formattedDate = booking.selectedDate
    ? format(new Date(booking.selectedDate + 'T12:00:00'), 'EEEE, MMMM d, yyyy', { locale: dateLocale })
    : '';

  const userDisplayName = !booking.isAgentReservation ? (authState.name ?? '').trim() : '';

  const hasPreferences = !!(booking.allergies || booking.veggie || booking.kids || booking.pets || booking.accessibility);
  const hasPairings = booking.selectedPairings.length > 0;
  const hasGuarantee = booking.guaranteeType !== 'traditional' && booking.feeDeposit > 0;

  const toleranceText = (isWaitingList ? t('GRAB_CONFIRMATION_WL_TOLERANCE') : t('GRAB_CONFIRMATION_TOLERANCE')).trim();

  const showOccasion = !!restaurant.allowOccasion;
  const showPromoCode = !!restaurant.allowPromoCode;
  const showServiceLanguage = !!restaurant.allowServiceLanguage;
  const showCompanions = !!restaurant.allowCompanions && booking.diners > 1;
  const maxCompanions = Math.max(0, booking.diners - 1);

  const handleSelectRegular = (regular: RegularCompanion) => {
    if (companions.length >= maxCompanions) return;
    setCompanions((prev) => [
      ...prev,
      {
        name: regular.name,
        email: regular.email,
        phone: regular.phone,
        id: regular.id,
        isRegular: true,
        notes: regular.notes,
        treatment: regular.treatment,
        relationship: regular.relationship,
      },
    ]);
    setAvailableRegulars((prev) => prev.filter((r) => r.id !== regular.id));
  };

  const handleCompanionRemoved = (_index: number, companion: CompanionData) => {
    if (companion.isRegular && companion.id) {
      const original = regularCompanions.find((r) => r.id === companion.id);
      if (original) {
        setAvailableRegulars((prev) => [...prev, original]);
      }
    }
  };

  const handleFinish = async () => {
    if (!booking.reservationId) {
      booking.resetBooking();
      navigate('/');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Save note and companions in parallel
      const promises: Promise<void>[] = [
        saveReservationNote(currentLanguage, {
          resId: booking.reservationId,
          note: note.trim(),
          occasion: occasion || undefined,
          languageService: languageService || undefined,
          promoCode: promoCode.trim() || undefined,
        }),
      ];

      // Save companions if any have names
      const validCompanions = companions.filter((c) => c.name.trim());
      if (validCompanions.length > 0) {
        promises.push(
          saveCompanions({
            reservationId: booking.reservationId,
            companions: validCompanions,
          }),
        );
      }

      await Promise.all(promises);

      booking.setUserNote(note.trim());

      toast.success(t('GRAB_CONFIRMATION_SAVED'), {
        icon: <Check className="w-4 h-4" />,
        duration: 2000,
      });

      // Brief delay so the user sees the success toast before navigating away
      await new Promise((resolve) => setTimeout(resolve, 1500));

      booking.resetBooking();
      navigate('/');
    } catch {
      setError(t('GRAB_CONFIRMATION_SAVE_ERROR'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <BookingPageLayout
      title=""
      progress={100}
      showBack={false}
    >
      <div className="w-full sm:px-6 flex flex-col items-center space-y-6">
        {/* Success / WL Icon */}
        <div data-testid="booking-success" className="animate-fade-in">
          {isWaitingList || isEarlyAccess ? (
            <Clock className="w-20 h-20 text-amber-500" strokeWidth={1.5} />
          ) : (
            <CheckCircle className="w-20 h-20 text-green-500" strokeWidth={1.5} />
          )}
        </div>

        <h2 className="text-3xl font-display text-center">
          {isWaitingList
            ? t('WAITING_LIST_MODAL_CONFIRMATION_DIALOG_TITLE')
            : isEarlyAccess
              ? t('EARLY_ACCESS_CONFIRMED')
              : t('GRAB_CONFIRMATION_TITLE')}
        </h2>

        {booking.uniqueId && (
          <div data-testid="reservation-id" className="bg-foreground/5 rounded-xl p-4 text-center">
            <p className="text-sm text-foreground/50">{t('GRAB_CONFIRMATION_RESERVATION_ID')}</p>
            <p className="text-2xl font-semibold text-foreground tracking-wider mt-1">
              {booking.uniqueId}
            </p>
          </div>
        )}

        {isWaitingList && booking.wlDates.length > 0 && (
          <div className="bg-foreground/5 rounded-xl p-4 w-full space-y-2">
            <p className="text-sm text-foreground/50">{t('BOOKING_FINAL_REQUESTED_DATES')}</p>
            <div className="flex flex-wrap gap-2">
              {booking.wlDates.map((d) => (
                <span key={d} className="text-sm font-medium text-foreground bg-foreground/10 px-3 py-1 rounded-full">
                  {d}
                </span>
              ))}
            </div>
          </div>
        )}

        <p className="text-foreground/50 text-sm text-center">
          {isWaitingList
            ? t('BOOKING_FINAL_WAITING_LIST_NOTICE')
            : isEarlyAccess
              ? t('EARLY_ACCESS_REQUESTED_MESSAGE')
              : booking.isAgentReservation && booking.guestEmail
                ? t('GRAB_CONFIRMATION_AGENT_EMAIL_SENT', { email: booking.guestEmail })
                : t('GRAB_CONFIRMATION_EMAIL_SENT')}
        </p>

        {/* Reservation Summary */}
        <div className="bg-foreground/5 rounded-xl p-5 space-y-3 w-full">
          <h3 className="font-display text-lg font-semibold text-foreground">
            {t('SUMMARY_YOUR_RESERVATION')}
          </h3>
          {userDisplayName && (
            <SummaryRow label={t('profile:MY_ACCOUNT_LABEL_NAME')} value={userDisplayName} />
          )}
          <SummaryRow label={t('SUMMARY_GUESTS')} value={t('PARTYSIZE_LABEL', { count: booking.diners })} />
          {isWaitingList && booking.wlDates.length > 0 ? (
            <div className="flex justify-between items-start">
              <span className="text-sm text-foreground/50">{t('SUMMARY_DATE')}</span>
              <div className="flex flex-wrap gap-1 justify-end max-w-[60%]">
                {booking.wlDates.map((d) => (
                  <span key={d} className="text-sm font-medium text-foreground bg-foreground/10 px-2 py-0.5 rounded">
                    {format(new Date(d + 'T12:00:00'), 'MMM d', { locale: dateLocale })}
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <SummaryRow label={t('SUMMARY_DATE')} value={formattedDate} />
          )}
          {booking.selectedTime && (
            <SummaryRow label={t('SUMMARY_TIME')} value={booking.selectedTime} />
          )}
          {booking.service && (
            <SummaryRow label={t('SUMMARY_SERVICE')} value={t(`FILTER_STRING_${booking.service.toUpperCase()}`)} />
          )}
          {booking.selectedExperience && (
            <SummaryRow
              label={t('SUMMARY_EXPERIENCE')}
              value={
                restaurant.menus?.[currentLanguage]?.find(
                  (m: { id: number; name: string }) => String(m.id) === booking.selectedExperience!.id
                )?.name ?? booking.selectedExperience.title
              }
            />
          )}
        </div>

        {/* Preferences Summary */}
        {hasPreferences && (
          <div className="bg-foreground/5 rounded-xl p-5 space-y-3 w-full">
            <h3 className="font-display text-lg font-semibold text-foreground">
              {t('CONFIRMATION_PREFERENCES')}
            </h3>
            {booking.allergies && (
              <SummaryRow label={t('STRING_MENU_ALLERGIES_TITLE')} value={booking.allergies} />
            )}
            {booking.veggie && (
              <SummaryRow label={t('NEW_MODAL_GUESTS_VEGETARIAN')} value={String(booking.howManyVeggie)} />
            )}
            {booking.kids && (
              <SummaryRow label={t('NEW_MODAL_GUESTS_CHILDREN')} value={String(booking.howManyKids)} />
            )}
            {booking.pets && (
              <SummaryRow label={t('NEW_MODAL_PET')} value={t('STRING_YES')} />
            )}
            {booking.accessibility && (
              <SummaryRow label={t('NEW_MODAL_ACCESSIBILITY_TABLE')} value={t('STRING_YES')} />
            )}
          </div>
        )}

        {/* Pairings Summary */}
        {hasPairings && (
          <div className="bg-foreground/5 rounded-xl p-5 space-y-3 w-full">
            <h3 className="font-display text-lg font-semibold text-foreground">
              {t('CONFIRMATION_PAIRINGS')}
            </h3>
            {booking.selectedPairings.map((p) => {
              const resolvedName = resolvePairingName(
                p.id,
                p.name,
                restaurant.beveragePairings,
                currentLanguage,
              );
              return (
                <SummaryRow
                  key={p.id}
                  label={`${resolvedName} (x${p.quantity})`}
                  value={`${displayCurrency} ${(p.price * p.quantity).toLocaleString()}`}
                />
              );
            })}
          </div>
        )}

        {/* Agent: Guest Info Summary */}
        {booking.isAgentReservation && booking.guestName && (
          <GuestDetailsCard
            name={booking.guestName}
            email={booking.guestEmail}
            phone={booking.guestPhone}
            className="w-full"
          />
        )}

        {/* Guarantee Info */}
        {hasGuarantee && (
          <div className="bg-foreground/5 rounded-xl p-5 space-y-2 w-full">
            <h3 className="font-display text-lg font-semibold text-foreground">
              {t('CONFIRMATION_GUARANTEE')}
            </h3>
            <p className="text-sm text-foreground/70">
              {booking.guaranteeType === 'deposit'
                ? t('CONFIRMATION_DEPOSIT_GUARANTEE', { amount: booking.feeDeposit, currency: displayCurrency })
                : t('CONFIRMATION_NO_SHOW_GUARANTEE', { amount: booking.feeDeposit, currency: displayCurrency })}
            </p>
          </div>
        )}

        {/* Tolerance Notice */}
        {toleranceText && (
          <div className="flex items-start gap-2 w-full text-sm text-foreground/50">
            <Clock className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5} />
            <p>{toleranceText}</p>
          </div>
        )}

        {/* Special Requirements / Notes */}
        <div className="w-full space-y-2">
          <label className="text-sm font-medium text-foreground/70">
            {t('NEW_MODAL_SPECIAL_REQUIREMENTS')}
          </label>
          <textarea
            className="w-full rounded-xl border border-border p-3 text-foreground focus:border-brand focus:outline-none resize-none"
            rows={3}
            placeholder={t('NEW_MODAL_WRITE_A_NOTE_HERE')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        {/* Companions */}
        {showCompanions && (
          <div className="w-full space-y-3">
            {availableRegulars.length > 0 && (
              <div className={`space-y-1 ${companions.length >= maxCompanions ? 'opacity-50' : ''}`}>
                <label className="text-sm font-medium text-foreground/70">
                  {t('GRAB_CONFIRMATION_ADD_REGULAR_GUEST')}
                </label>
                <p className="text-sm text-foreground/60">
                  {availableRegulars.map((regular, idx) => (
                    <span key={regular.id}>
                      <button
                        type="button"
                        disabled={companions.length >= maxCompanions}
                        onClick={() => handleSelectRegular(regular)}
                        className="underline text-brand hover:text-brand-dark transition-colors disabled:cursor-not-allowed disabled:text-foreground/40"
                      >
                        {regular.name}
                      </button>
                      {idx < availableRegulars.length - 1 && ', '}
                    </span>
                  ))}
                </p>
              </div>
            )}

            <CompanionInputs
              maxCompanions={maxCompanions}
              companions={companions}
              onChange={setCompanions}
              onRemove={handleCompanionRemoved}
            />
          </div>
        )}

        {/* Occasion */}
        {showOccasion && (
          <div className="w-full space-y-2">
            <label className="text-sm font-medium text-foreground/70">
              {t('NEW_MODAL_SPECIAL_OCCASION')}
            </label>
            <div className="flex flex-wrap gap-2">
              {OCCASIONS.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  className={`px-4 py-2 rounded-xl border text-sm transition-all duration-200 ${
                    occasion === o.value
                      ? 'bg-brand text-brand-foreground border-brand'
                      : 'bg-card text-foreground/70 border-border hover:border-brand-light'
                  }`}
                  onClick={() => setOccasion(occasion === o.value ? '' : o.value)}
                >
                  {t(o.labelKey)}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Promo Code */}
        {showPromoCode && (
          <div className="w-full space-y-2">
            <label className="text-sm font-medium text-foreground/70">
              {t('PROMOCODE_LABEL')}
            </label>
            <Input
              type="text"
              placeholder={t('PROMOCODE_PLACEHOLDER')}
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
              className="rounded-xl"
            />
          </div>
        )}

        {/* Service Language */}
        {showServiceLanguage && (
          <div className="w-full space-y-2">
            <label className="text-sm font-medium text-foreground/70">
              {t('NEW_MODAL_HOST_LANGUAGE')}
            </label>
            <div className="flex gap-2">
              {SERVICE_LANGUAGES.map((lang) => (
                <button
                  key={lang.value}
                  type="button"
                  className={`px-5 py-2 rounded-xl border text-sm transition-all duration-200 ${
                    languageService === lang.value
                      ? 'bg-brand text-brand-foreground border-brand'
                      : 'bg-card text-foreground/70 border-border hover:border-brand-light'
                  }`}
                  onClick={() => setLanguageService(languageService === lang.value ? '' : lang.value)}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 text-red-700 rounded-xl p-4 text-sm w-full">
            {error}
          </div>
        )}
      </div>

      <div className="w-full sm:px-6 mt-8">
        <Button
          data-testid="finish-btn"
          className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
          onClick={handleFinish}
          disabled={isSubmitting}
        >
          {isSubmitting ? t('GRAB_CONFIRMATION_SAVING') : t('CONFIRMATION_MESSAGE_RESERVATION_TEXT_10')}
        </Button>
      </div>
    </BookingPageLayout>
  );
};

export default BookingFinalPage;
